/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-21
 * Revision: 1.2
 * Description: Floating KT AI Assistant chat widget - accessible site-wide
 */

import React, { useState, useEffect, useRef } from 'react'
import { X, Send, Minimize2, Maximize2, Search, Plus } from 'lucide-react'
import { api, ChatMessage, ChatThread } from '@services/api'
import { useUserContext } from '@context/UserContext'

interface Size {
  width: number
  height: number
}

interface Position {
  x: number
  y: number
}

const MIN_WIDTH = 345
const MIN_HEIGHT = 450
const DEFAULT_WIDTH = 720 // 50% larger (was 480)
const DEFAULT_HEIGHT = 870 // 50% larger (was 580)

interface FloatingChatProps {
  isOpen: boolean
  onToggle: () => void
  /** 'test' renders the optional AI Test Assistant — separate session, endpoint, and accent color. */
  variant?: 'standard' | 'test'
}

const ACCENT = {
  standard: {
    title: 'KT AI Assistant',
    welcomeMessage: "Hello! I'm your KT AI Assistant. How can I help you today?",
    header: 'bg-blue-600',
    headerHover: 'hover:bg-blue-500',
    headerActive: 'bg-blue-500',
    userBubble: 'bg-blue-600',
    sendButton: 'bg-blue-600 hover:bg-blue-700',
    focusRing: 'focus:ring-blue-500',
    suggestedHover: 'hover:border-blue-300 hover:bg-blue-50',
  },
  test: {
    title: 'AI Test Assistant',
    welcomeMessage:
      "Hello! I'm the AI Test Assistant — I have access to RAG and MCP tool servers. How can I help you today?",
    header: 'bg-purple-600',
    headerHover: 'hover:bg-purple-500',
    headerActive: 'bg-purple-500',
    userBubble: 'bg-purple-600',
    sendButton: 'bg-purple-600 hover:bg-purple-700',
    focusRing: 'focus:ring-purple-500',
    suggestedHover: 'hover:border-purple-300 hover:bg-purple-50',
  },
} as const

const FloatingChat: React.FC<FloatingChatProps> = ({ isOpen, onToggle, variant = 'standard' }) => {
  const accent = ACCENT[variant]
  const { activeContext, switchContext } = useUserContext()
  const [isMinimized, setIsMinimized] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chatAvailable, setChatAvailable] = useState<boolean | null>(null)
  const [hasNewMessage, setHasNewMessage] = useState(false)
  // The widget keeps one persisted thread per conversation; "clear" starts a new one.
  const [currentThreadId, setCurrentThreadId] = useState<number | null>(null)
  const [currentThreadContextId, setCurrentThreadContextId] = useState<number | null>(null)
  const [currentThreadTitle, setCurrentThreadTitle] = useState<string | null>(null)
  const [showThreads, setShowThreads] = useState(false)
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [threadSearch, setThreadSearch] = useState('')
  const [size, setSize] = useState<Size>(() => ({
    width: Math.min(DEFAULT_WIDTH, window.innerWidth - 32),
    height: Math.min(DEFAULT_HEIGHT, window.innerHeight - 40),
  }))
  const [position, setPosition] = useState<Position | null>(null)
  const [isResizing, setIsResizing] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatWindowRef = useRef<HTMLDivElement>(null)
  const conversationGenerationRef = useRef(0)
  const previousContextIdRef = useRef(activeContext?.id)
  const pendingContextSwitchRef = useRef<number | null>(null)
  const dragStartRef = useRef<{
    mouseX: number
    mouseY: number
    posX: number
    posY: number
    width: number
    height: number
  } | null>(null)

  // Calculate center position on first open, clamping size to viewport
  useEffect(() => {
    if (isOpen && !position) {
      const w = Math.min(size.width, window.innerWidth - 32)
      const h = Math.min(size.height, window.innerHeight - 40)
      if (w !== size.width || h !== size.height) setSize({ width: w, height: h })
      const centerX = (window.innerWidth - w) / 2
      const centerY = (window.innerHeight - h) / 2
      setPosition({ x: Math.max(0, centerX), y: Math.max(20, centerY) })
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && chatAvailable === null) {
      checkChatStatus()
    }
  }, [isOpen])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (showThreads) {
      loadThreads(threadSearch)
    }
  }, [showThreads])

  useEffect(() => {
    const contextId = activeContext?.id
    if (previousContextIdRef.current === contextId) return

    previousContextIdRef.current = contextId
    const isSelectedThreadContext = pendingContextSwitchRef.current === contextId
    pendingContextSwitchRef.current = null
    if (!isSelectedThreadContext) {
      conversationGenerationRef.current += 1
      setLoading(false)
    }
    setCurrentThreadId(null)
    setCurrentThreadContextId(null)
    setCurrentThreadTitle(null)
    setMessages([])
    setInput('')
    setError(null)
    setShowThreads(false)
  }, [activeContext?.id])

  // Handle mouse and touch events for resize and drag
  useEffect(() => {
    const handleInteractionMove = (clientX: number, clientY: number) => {
      if (!dragStartRef.current) return

      if (isDragging && position) {
        const deltaX = clientX - dragStartRef.current.mouseX
        const deltaY = clientY - dragStartRef.current.mouseY
        const edgeMargin = window.innerWidth < 640 ? 16 : 0
        const newX = Math.max(
          edgeMargin,
          Math.min(window.innerWidth - size.width - edgeMargin, dragStartRef.current.posX + deltaX),
        )
        const newY = Math.max(0, Math.min(window.innerHeight - 50, dragStartRef.current.posY + deltaY))
        setPosition({ x: newX, y: newY })
      } else if (isResizing && position) {
        const deltaX = clientX - dragStartRef.current.mouseX
        const deltaY = clientY - dragStartRef.current.mouseY

        let newWidth = dragStartRef.current.width
        let newHeight = dragStartRef.current.height
        let newX = dragStartRef.current.posX
        let newY = dragStartRef.current.posY

        if (isResizing.includes('e')) {
          const maxWidth = window.innerWidth - dragStartRef.current.posX - 16
          newWidth = Math.max(MIN_WIDTH, Math.min(dragStartRef.current.width + deltaX, maxWidth))
        }
        if (isResizing.includes('w')) {
          const edgeMargin = window.innerWidth < 640 ? 16 : 0
          const widthChange = Math.min(deltaX, dragStartRef.current.width - MIN_WIDTH)
          newX = Math.max(edgeMargin, dragStartRef.current.posX + widthChange)
          newWidth = dragStartRef.current.posX + dragStartRef.current.width - newX
        }
        if (isResizing.includes('s')) {
          newHeight = Math.max(MIN_HEIGHT, dragStartRef.current.height + deltaY)
        }
        if (isResizing.includes('n')) {
          const heightChange = Math.min(deltaY, dragStartRef.current.height - MIN_HEIGHT)
          newHeight = dragStartRef.current.height - heightChange
          newY = dragStartRef.current.posY + heightChange
        }

        setSize({ width: newWidth, height: newHeight })
        setPosition({ x: newX, y: newY })
      }
    }

    const handleInteractionEnd = () => {
      setIsResizing(null)
      setIsDragging(false)
      dragStartRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    const handleMouseMove = (e: MouseEvent) => handleInteractionMove(e.clientX, e.clientY)
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (touch) {
        e.preventDefault()
        handleInteractionMove(touch.clientX, touch.clientY)
      }
    }

    if (isResizing || isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleInteractionEnd)
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleInteractionEnd)
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleInteractionEnd)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleInteractionEnd)
    }
  }, [isResizing, isDragging, position, size])

  const startResize = (e: React.MouseEvent, direction: string) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(direction)
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: position?.x || 0,
      posY: position?.y || 0,
      width: size.width,
      height: size.height,
    }
  }

  const startResizeTouch = (e: React.TouchEvent, direction: string) => {
    e.preventDefault()
    e.stopPropagation()
    const touch = e.touches[0]
    setIsResizing(direction)
    dragStartRef.current = {
      mouseX: touch.clientX,
      mouseY: touch.clientY,
      posX: position?.x || 0,
      posY: position?.y || 0,
      width: size.width,
      height: size.height,
    }
  }

  const startDrag = (e: React.MouseEvent) => {
    if (isMinimized || (e.target as HTMLElement).closest('button')) {
      return
    }
    e.preventDefault()
    setIsDragging(true)
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: position?.x || 0,
      posY: position?.y || 0,
      width: size.width,
      height: size.height,
    }
  }

  const startDragTouch = (e: React.TouchEvent) => {
    if (isMinimized || (e.target as HTMLElement).closest('button')) return
    e.preventDefault()
    const touch = e.touches[0]
    setIsDragging(true)
    dragStartRef.current = {
      mouseX: touch.clientX,
      mouseY: touch.clientY,
      posX: position?.x || 0,
      posY: position?.y || 0,
      width: size.width,
      height: size.height,
    }
  }

  const checkChatStatus = async () => {
    try {
      const status = await api.getChatStatus()
      setChatAvailable(status.available)

      if (status.available && messages.length === 0) {
        setMessages([
          {
            role: 'assistant',
            content: accent.welcomeMessage,
          },
        ])
      } else if (!status.available) {
        setError(status.message)
      }
    } catch (err) {
      setError('Failed to connect to chatbot service')
      setChatAvailable(false)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const loadThreads = async (search?: string) => {
    try {
      setThreads(await api.listChatThreads(search))
    } catch (err) {
      console.error('Failed to load chat threads:', err)
    }
  }

  const handleThreadSearchChange = (value: string) => {
    setThreadSearch(value)
    loadThreads(value)
  }

  const handleSelectThread = async (thread: ChatThread) => {
    const requestGeneration = ++conversationGenerationRef.current
    setLoading(true)
    setError(null)
    try {
      // The thread may belong to a different context than the one currently active —
      // switch first so the header on getChatThread matches what the thread expects.
      if (thread.context_id !== activeContext?.id) {
        pendingContextSwitchRef.current = thread.context_id
      }
      switchContext(thread.context_id)
      const { messages: threadMessages } = await api.getChatThread(thread.id)
      if (requestGeneration !== conversationGenerationRef.current) return
      setCurrentThreadId(thread.id)
      setCurrentThreadContextId(thread.context_id)
      setCurrentThreadTitle(thread.title)
      setMessages(
        threadMessages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content })),
      )
      setShowThreads(false)
    } catch (err) {
      if (requestGeneration !== conversationGenerationRef.current) return
      setError('Failed to load that chat thread')
      console.error('Failed to load chat thread:', err)
    } finally {
      if (requestGeneration === conversationGenerationRef.current) {
        setLoading(false)
      }
    }
  }

  const handleSend = async () => {
    if (!input.trim() || loading) {
      return
    }

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setLoading(true)
    setError(null)
    const requestGeneration = conversationGenerationRef.current

    try {
      if (variant === 'test') {
        // The AI Test Assistant has no persisted threads: MCP tool access lives on
        // POST /api/chat/test, which is stateless, so the last 20 messages are
        // resent each turn to carry multi-turn context.
        const history = [...messages, userMessage].slice(-20)
        const response = await api.sendTestChatMessage(history)
        if (requestGeneration !== conversationGenerationRef.current) return
        setMessages((prev) => [...prev, { role: 'assistant', content: response.message }])
      } else {
        let threadId = currentThreadContextId === activeContext?.id ? currentThreadId : null
        if (threadId === null) {
          const thread = await api.createChatThread(userMessage.content.slice(0, 60))
          if (requestGeneration !== conversationGenerationRef.current) return
          threadId = thread.id
          setCurrentThreadId(threadId)
          setCurrentThreadContextId(thread.context_id)
          setCurrentThreadTitle(thread.title)
        }

        const { assistantMessage } = await api.sendThreadMessage(threadId, userMessage.content)
        if (requestGeneration !== conversationGenerationRef.current) return

        setMessages((prev) => [...prev, { role: 'assistant', content: assistantMessage.content }])
      }

      if (isMinimized) {
        setHasNewMessage(true)
      }
    } catch (err: any) {
      if (requestGeneration !== conversationGenerationRef.current) return
      // Roll back the optimistic user message — otherwise it stays in history and
      // gets resent with every later request, so a deterministic backend failure
      // (e.g. a bad prompt) permanently breaks the conversation.
      setMessages((prev) => prev.filter((m) => m !== userMessage))
      setInput(userMessage.content)
      setError(err.response?.data?.error || 'Failed to send message')
      console.error('Chat error:', err)
    } finally {
      if (requestGeneration === conversationGenerationRef.current) {
        setLoading(false)
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const toggleChat = () => {
    if (!isOpen) {
      // Reset position to center when opening, clamped to viewport
      const clampedWidth = Math.min(size.width, window.innerWidth - 32)
      const clampedHeight = Math.min(size.height, window.innerHeight - 40)
      if (clampedWidth !== size.width || clampedHeight !== size.height) {
        setSize({ width: clampedWidth, height: clampedHeight })
      }
      const centerX = (window.innerWidth - clampedWidth) / 2
      const centerY = (window.innerHeight - clampedHeight) / 2
      setPosition({ x: Math.max(0, centerX), y: Math.max(20, centerY) })
    }
    onToggle()
    setIsMinimized(false)
    setHasNewMessage(false)
  }

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized)
    if (!isMinimized) {
      setHasNewMessage(false)
    }
  }

  const clearHistory = () => {
    conversationGenerationRef.current += 1
    pendingContextSwitchRef.current = null
    setLoading(false)
    setCurrentThreadId(null)
    setCurrentThreadContextId(null)
    setCurrentThreadTitle(null)
    setShowThreads(false)
    setInput('')
    setMessages([
      {
        role: 'assistant',
        content: accent.welcomeMessage,
      },
    ])
    setError(null)
  }

  const suggestedQuestions = [
    'How many systems are in the database?',
    'Show me all systems with high security scores',
    'What products are most commonly used?',
  ]

  return (
    <>
      {/* Backdrop with blur when chat is open */}
      {isOpen && !isMinimized && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-all duration-300"
          onClick={toggleChat}
        />
      )}

      {/* Chat Window */}
      {isOpen && position && (
        <div
          ref={chatWindowRef}
          className={`fixed z-50 flex flex-col rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800 dark:shadow-none ${
            isMinimized ? 'h-14 w-72' : ''
          }`}
          style={
            isMinimized
              ? { top: 'auto', left: 'auto', bottom: 0, right: 24 }
              : {
                  width: size.width,
                  height: size.height,
                  minWidth: MIN_WIDTH,
                  left: position.x,
                  top: position.y,
                  right: 'auto',
                  bottom: 'auto',
                  maxWidth: 'calc(100vw - 32px)',
                  maxHeight: 'calc(100vh - 40px)',
                }
          }
        >
          {/* Resize handles */}
          {!isMinimized && (
            <>
              {/* Edges */}
              <div
                className="absolute top-0 right-2 left-2 h-1 cursor-n-resize"
                onMouseDown={(e) => startResize(e, 'n')}
                onTouchStart={(e) => startResizeTouch(e, 'n')}
              />
              <div
                className="absolute right-2 bottom-0 left-2 h-1 cursor-s-resize"
                onMouseDown={(e) => startResize(e, 's')}
                onTouchStart={(e) => startResizeTouch(e, 's')}
              />
              <div
                className="absolute top-2 bottom-2 left-0 w-1 cursor-w-resize"
                onMouseDown={(e) => startResize(e, 'w')}
                onTouchStart={(e) => startResizeTouch(e, 'w')}
              />
              <div
                className="absolute top-2 right-0 bottom-2 w-1 cursor-e-resize"
                onMouseDown={(e) => startResize(e, 'e')}
                onTouchStart={(e) => startResizeTouch(e, 'e')}
              />

              {/* Corners */}
              <div
                className="absolute top-0 left-0 h-3 w-3 cursor-nw-resize"
                onMouseDown={(e) => startResize(e, 'nw')}
                onTouchStart={(e) => startResizeTouch(e, 'nw')}
              />
              <div
                className="absolute top-0 right-0 h-3 w-3 cursor-ne-resize"
                onMouseDown={(e) => startResize(e, 'ne')}
                onTouchStart={(e) => startResizeTouch(e, 'ne')}
              />
              <div
                className="absolute bottom-0 left-0 h-3 w-3 cursor-sw-resize"
                onMouseDown={(e) => startResize(e, 'sw')}
                onTouchStart={(e) => startResizeTouch(e, 'sw')}
              />
              <div
                className="absolute right-0 bottom-0 h-3 w-3 cursor-se-resize"
                onMouseDown={(e) => startResize(e, 'se')}
                onTouchStart={(e) => startResizeTouch(e, 'se')}
              >
                <svg
                  className="absolute right-0.5 bottom-0.5 h-3 w-3 text-gray-400 dark:text-gray-500"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
                </svg>
              </div>
            </>
          )}

          {/* Header - Draggable */}
          <div
            className={`flex flex-shrink-0 items-center justify-between rounded-t-xl px-4 py-3 text-white select-none ${accent.header} ${isMinimized ? 'cursor-default' : 'cursor-move'}`}
            onMouseDown={startDrag}
            onTouchStart={startDragTouch}
          >
            <div className="flex items-center gap-2">
              <img src="/dolly.png" alt="Dolly the Sheep" className="pointer-events-none h-8 w-8 rounded-full" />
              <div className="pointer-events-none">
                <h3 className="text-sm font-semibold">{accent.title}</h3>
                {!isMinimized && currentThreadTitle && (
                  <p className="max-w-[200px] truncate text-xs text-blue-100">{currentThreadTitle}</p>
                )}
                {!isMinimized && !currentThreadTitle && (
                  <p className="text-xs text-blue-100">Drag to move | Resize from edges</p>
                )}
              </div>
              {hasNewMessage && isMinimized && (
                <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-xs">New</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {/* Saved chats are thread-backed, which the stateless test assistant has no equivalent for. */}
              {!isMinimized && variant !== 'test' && (
                <button
                  onClick={() => setShowThreads((v) => !v)}
                  className={`rounded px-2 py-2 text-xs font-medium whitespace-nowrap transition-colors ${accent.headerHover} ${showThreads ? accent.headerActive : ''}`}
                >
                  {showThreads ? 'Hide saved chats' : 'Show saved chats'}
                </button>
              )}
              <button
                onClick={toggleMinimize}
                className={`rounded p-2 transition-colors ${accent.headerHover}`}
                title={isMinimized ? 'Maximize' : 'Minimize'}
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </button>
              <button
                onClick={toggleChat}
                className={`rounded p-2 transition-colors ${accent.headerHover}`}
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Chat Content - Hidden when minimized */}
          {!isMinimized && (
            <>
              {/* Error Alert */}
              {error && (
                <div className="flex flex-shrink-0 items-center justify-between border-b border-red-200 bg-red-50 px-4 py-2 dark:border-red-800/50">
                  <p className="text-sm text-red-600">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="ml-2 rounded p-1 text-red-400 transition-colors hover:bg-red-100 hover:text-red-600"
                    title="Dismiss error"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Messages Area */}
              <div className="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4 dark:bg-white/5">
                {showThreads ? (
                  <div className="flex h-full flex-col">
                    <div className="mb-3 flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={threadSearch}
                          onChange={(e) => handleThreadSearchChange(e.target.value)}
                          placeholder="Search chats..."
                          className="w-full rounded-lg border border-gray-300 py-2 pr-3 pl-8 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        />
                      </div>
                      <button
                        onClick={clearHistory}
                        className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        title="New chat"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    {threads.length === 0 ? (
                      <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No saved chats yet</p>
                    ) : (
                      <div className="space-y-1 overflow-y-auto">
                        {threads.map((thread) => (
                          <button
                            key={thread.id}
                            onClick={() => handleSelectThread(thread)}
                            className={`block w-full truncate rounded-lg px-3 py-2 text-left text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 ${
                              thread.id === currentThreadId
                                ? 'bg-blue-100 dark:bg-blue-900/40'
                                : 'bg-white dark:bg-gray-800'
                            }`}
                          >
                            {thread.context_display_name
                              ? `${thread.context_display_name}: ${thread.title}`
                              : thread.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : chatAvailable === null ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="py-8 text-center">
                    <img src="/dolly.png" alt="Dolly the Sheep" className="mx-auto mb-4 h-16 w-16 rounded-full" />
                    <p className="mb-4 text-gray-600 dark:text-gray-400">Try asking:</p>
                    <div className="space-y-2">
                      {suggestedQuestions.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => setInput(q)}
                          className={`block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm transition-colors dark:border-gray-700 dark:bg-gray-800 ${accent.suggestedHover}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((message, index) => (
                    <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex max-w-[85%] gap-2 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        {message.role === 'assistant' && (
                          <img src="/dolly.png" alt="Dolly the Sheep" className="h-8 w-8 flex-shrink-0 rounded-full" />
                        )}
                        <div
                          className={`rounded-lg px-3 py-2 text-sm ${
                            message.role === 'user'
                              ? `${accent.userBubble} text-white`
                              : 'border border-gray-200 bg-white text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                          }`}
                        >
                          <p className="break-words whitespace-pre-wrap">{message.content}</p>
                        </div>
                        {message.role === 'user' && (
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-300 dark:bg-gray-600">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-200">You</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}

                {loading && (
                  <div className="flex justify-start">
                    <div className="flex gap-2">
                      <img src="/dolly.png" alt="Dolly the Sheep" className="h-8 w-8 rounded-full" />
                      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                        <div className="flex gap-1">
                          <span
                            className="h-2 w-2 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"
                            style={{ animationDelay: '0ms' }}
                          />
                          <span
                            className="h-2 w-2 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"
                            style={{ animationDelay: '150ms' }}
                          />
                          <span
                            className="h-2 w-2 animate-bounce rounded-full bg-gray-400 dark:bg-gray-500"
                            style={{ animationDelay: '300ms' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              {!showThreads && (
                <div className="flex-shrink-0 rounded-b-xl border-t border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex gap-2">
                    <button
                      onClick={clearHistory}
                      disabled={messages.length <= 1}
                      className={`rounded-lg px-3 py-2 text-gray-500 transition-colors disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400 ${accent.suggestedHover}`}
                      title="Start new chat"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything..."
                      disabled={loading || !chatAvailable}
                      rows={1}
                      className={`flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:disabled:bg-gray-800 ${accent.focusRing}`}
                    />
                    <button
                      onClick={handleSend}
                      disabled={!input.trim() || loading || !chatAvailable}
                      className={`rounded-lg px-4 py-2 text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600 ${accent.sendButton}`}
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Press Enter to send</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}

export default FloatingChat
